import { createFileRoute } from '@tanstack/react-router'
import { Helmet } from 'react-helmet-async'

const heading: React.CSSProperties = { fontSize: 36, fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 8 }
const updated: React.CSSProperties = { fontSize: 13, color: '#777', fontFamily: 'var(--oe-font-mono)', marginBottom: 40, fontWeight: 500 }
const h2: React.CSSProperties = { fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em', marginTop: 48, marginBottom: 16 }
const h3: React.CSSProperties = { fontSize: 17, fontWeight: 700, marginTop: 32, marginBottom: 12 }
const p: React.CSSProperties = { fontSize: 16, lineHeight: 1.8, color: '#4a4745', marginBottom: 16, fontWeight: 500 }
const ul: React.CSSProperties = { fontSize: 16, lineHeight: 1.8, color: '#4a4745', marginBottom: 16, paddingLeft: 24, listStyleType: 'disc', fontWeight: 500 }
const li: React.CSSProperties = { marginBottom: 8 }

const tableWrapper: React.CSSProperties = { overflowX: 'auto', marginBottom: 24 }
const table: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 14, lineHeight: 1.6 }
const th: React.CSSProperties = { textAlign: 'left', fontWeight: 700, padding: '10px 12px', borderBottom: '2px solid #e8e0d8', color: '#1a1216' }
const td: React.CSSProperties = { padding: '10px 12px', borderBottom: '1px solid #e8e0d8', color: '#4a4745', fontWeight: 500 }

const lnk: React.CSSProperties = { color: '#1a1216', textDecoration: 'underline', textUnderlineOffset: 2 }

function ResponsibleDisclosurePage() {
  return (
    <article>
      <Helmet>
        <title>Responsible Disclosure | One-Email</title>
        <meta
          name="description"
          content="One-Email's responsible disclosure policy for security researchers. Report vulnerabilities safely under our safe harbor framework."
        />
        <link rel="canonical" href="https://one-email.com/security/responsible-disclosure" />
      </Helmet>

      <h1 style={heading}>Responsible Disclosure Policy</h1>
      <p style={updated}>Last updated: March 24, 2026</p>

      <p style={p}>
        If you find a security vulnerability in One-Email, we want to hear about it. Report it
        responsibly and we'll fix it quickly. We will not pursue legal action against researchers who
        follow this policy.
      </p>

      <h2 style={h2}>Safe Harbor</h2>
      <p style={p}>We consider security research conducted under this policy to be:</p>
      <ul style={ul}>
        <li style={li}>
          <strong>Authorized</strong> under the Computer Fraud and Abuse Act (CFAA) and similar laws
        </li>
        <li style={li}>
          <strong>Exempt</strong> from DMCA restrictions on circumvention of security controls
        </li>
        <li style={li}>
          <strong>Lawful</strong> under applicable anti-hacking statutes, provided you comply with
          this policy
        </li>
      </ul>
      <p style={p}>
        We will <strong>not pursue legal action</strong> against researchers who discover and report
        vulnerabilities in good faith, in compliance with this policy.
      </p>
      <p style={p}>
        If legal action is initiated by a third party against you for activities conducted under this
        policy, we will take steps to make it known that your actions were conducted in compliance
        with this policy.
      </p>

      <h2 style={h2}>Scope</h2>

      <h3 style={h3}>In Scope</h3>
      <ul style={ul}>
        <li style={li}>
          <code>api.one-email.com</code> — Production API
        </li>
        <li style={li}>
          <code>dashboard.one-email.com</code> — Customer dashboard
        </li>
        <li style={li}>
          <code>one-email.com</code> — Marketing site
        </li>
        <li style={li}>
          One-Email SDKs (<code>one-email</code> npm package, Python SDK, Go SDK, Rust SDK)
        </li>
        <li style={li}>One-Email CLI</li>
      </ul>

      <h3 style={h3}>Out of Scope</h3>
      <ul style={ul}>
        <li style={li}>
          Third-party services we use (Cloudflare, Neon, Scaleway, Polar) — report vulnerabilities
          in these services directly to them
        </li>
        <li style={li}>
          Social engineering, phishing, or physical attacks against One-Email staff
        </li>
        <li style={li}>Denial of Service (DoS/DDoS) attacks</li>
        <li style={li}>Automated scanning or fuzzing without prior coordination</li>
        <li style={li}>Spam or email abuse testing</li>
        <li style={li}>Clickjacking on pages without sensitive actions</li>
        <li style={li}>
          Missing security headers that do not lead to a demonstrable exploit
        </li>
        <li style={li}>Password brute-force attacks</li>
        <li style={li}>
          Known vulnerabilities in third-party libraries without a demonstrated exploit path
        </li>
        <li style={li}>
          Vulnerabilities requiring unlikely or extreme conditions (e.g., physical access to a
          victim's unlocked device)
        </li>
      </ul>

      <h2 style={h2}>Rules of Engagement</h2>
      <p style={p}>To qualify for safe harbor, you must:</p>
      <ol
        style={{
          fontSize: 16,
          lineHeight: 1.8,
          color: '#4a4745',
          marginBottom: 16,
          paddingLeft: 24,
          fontWeight: 500,
        }}
      >
        <li style={li}>
          <strong>Do not access or modify other users' data.</strong> If you accidentally access
          another user's data, stop immediately and report it.
        </li>
        <li style={li}>
          <strong>Do not perform destructive actions.</strong> Do not delete data, modify
          configurations, or disrupt service for other users.
        </li>
        <li style={li}>
          <strong>Do not exfiltrate data.</strong> Retrieve only the minimum information needed to
          demonstrate the vulnerability.
        </li>
        <li style={li}>
          <strong>Do not run automated scans</strong> against production systems without prior
          written coordination.
        </li>
        <li style={li}>
          <strong>Do not disclose the vulnerability publicly</strong> until we have had a reasonable
          opportunity to fix it (see timeline below).
        </li>
        <li style={li}>
          <strong>Only test against your own accounts.</strong> Create a test account if needed.
        </li>
      </ol>

      <h2 style={h2}>How to Report</h2>
      <p style={p}>
        Send your report to{' '}
        <a href="mailto:security@one-email.com" style={lnk}>
          <strong>security@one-email.com</strong>
        </a>{' '}
        with:
      </p>
      <ul style={ul}>
        <li style={li}>Description of the vulnerability</li>
        <li style={li}>Steps to reproduce</li>
        <li style={li}>Impact assessment (what could an attacker do?)</li>
        <li style={li}>
          Any supporting evidence (screenshots, logs, proof-of-concept code)
        </li>
        <li style={li}>Your contact information (for follow-up questions)</li>
      </ul>
      <p style={p}>
        If your report contains sensitive information, please note that in the subject line and we
        will provide a secure channel for the full details.
      </p>

      <h2 style={h2}>Response Timeline</h2>
      <div style={tableWrapper}>
        <table style={table}>
          <thead>
            <tr>
              <th style={th}>Stage</th>
              <th style={th}>Timeline</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={td}>Acknowledgment of receipt</td>
              <td style={td}>
                Within <strong>3 business days</strong>
              </td>
            </tr>
            <tr>
              <td style={td}>Initial triage and assessment</td>
              <td style={td}>
                Within <strong>10 business days</strong>
              </td>
            </tr>
            <tr>
              <td style={td}>Status update to reporter</td>
              <td style={td}>
                Within <strong>14 business days</strong>
              </td>
            </tr>
            <tr>
              <td style={td}>Resolution target (Critical severity)</td>
              <td style={td}>30 days</td>
            </tr>
            <tr>
              <td style={td}>Resolution target (High severity)</td>
              <td style={td}>60 days</td>
            </tr>
            <tr>
              <td style={td}>Resolution target (Medium/Low severity)</td>
              <td style={td}>90 days</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p style={p}>
        We will keep you informed of our progress. If we need more time, we will communicate the
        reason and an updated timeline.
      </p>

      <h2 style={h2}>Recognition</h2>
      <p style={p}>
        We maintain a <strong>Hall of Fame</strong> page acknowledging researchers who have reported
        valid vulnerabilities. With your permission, we will list:
      </p>
      <ul style={ul}>
        <li style={li}>Your name (or handle)</li>
        <li style={li}>An optional link to your website or profile</li>
        <li style={li}>The date of your report</li>
      </ul>
      <p style={p}>
        We do not currently offer monetary bounties. We may introduce a bug bounty program in the
        future as our security program matures. If we do, we will announce it here.
      </p>

      <h2 style={h2}>Severity Classification</h2>
      <div style={tableWrapper}>
        <table style={table}>
          <thead>
            <tr>
              <th style={th}>Severity</th>
              <th style={th}>Examples</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={{ ...td, fontWeight: 700 }}>Critical</td>
              <td style={td}>
                Remote code execution, authentication bypass, SQL injection, access to other
                customers' data
              </td>
            </tr>
            <tr>
              <td style={{ ...td, fontWeight: 700 }}>High</td>
              <td style={td}>
                Stored XSS, CSRF on sensitive actions, privilege escalation, DKIM key exposure
              </td>
            </tr>
            <tr>
              <td style={{ ...td, fontWeight: 700 }}>Medium</td>
              <td style={td}>
                Reflected XSS, information disclosure (error messages revealing internals), insecure
                direct object references
              </td>
            </tr>
            <tr>
              <td style={{ ...td, fontWeight: 700 }}>Low</td>
              <td style={td}>
                Missing security headers with limited impact, verbose error messages, minor
                information leakage
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2 style={h2}>Contact</h2>
      <p style={p}>
        <strong>Security reports:</strong>{' '}
        <a href="mailto:security@one-email.com" style={lnk}>
          security@one-email.com
        </a>
      </p>
      <p style={p}>
        <strong>General security questions:</strong>{' '}
        <a href="mailto:security@one-email.com" style={lnk}>
          security@one-email.com
        </a>
      </p>

      <p
        style={{
          fontSize: 14,
          color: '#999',
          marginTop: 48,
          fontStyle: 'italic',
          fontWeight: 500,
        }}
      >
        This policy is based on the{' '}
        <a
          href="https://disclose.io"
          target="_blank"
          rel="noopener noreferrer"
          style={lnk}
        >
          disclose.io
        </a>{' '}
        safe harbor framework.
      </p>
    </article>
  )
}

export const Route = createFileRoute('/security/responsible-disclosure')({
  component: ResponsibleDisclosurePage,
})
